/**
 * Ingredient Glossary Component with Pagination
 * Handles alphabetical filtering and load-more functionality
 * @module IngredientGlossary
 */

class IngredientGlossary extends HTMLElement {
    constructor() {
      super();
      
      this.letterButtons = this.querySelectorAll('.glossary-letter');
      this.loadMoreButton = this.querySelector('[data-load-more]');
      this.resultsContainer = this.querySelector('[data-results-container]');
      this.alphabetNav = this.querySelector('[data-alphabet-nav]');
      
      this.currentFilter = 'all';
      this.itemsPerPage = parseInt(this.dataset.itemsPerPage) || 20;
      this.initialLetter = this.dataset.initialLetter || 'all';
      this.currentPage = 1;
      this.filteredItems = [];
      
      // Get all glossary items - use querySelectorAll on the results container
      this.glossaryItems = Array.from(
        this.resultsContainer?.querySelectorAll('.glossary-item') || 
        this.querySelectorAll('.glossary-item')
      );
      
      this.init();
    }
  
    /**
     * Refresh the glossary items list
     */
    refreshItemsList() {
      this.glossaryItems = Array.from(
        this.resultsContainer?.querySelectorAll('.glossary-item') || 
        this.querySelectorAll('.glossary-item')
      );
      return this.glossaryItems.length;
    }
  
    /**
     * Initialize component
     */
    init() {
      // Refresh items list before initializing
      this.refreshItemsList();
      
      this.checkAvailableLetters();
      this.bindEvents();
      this.initStickyNavigation();
      
      // Handle initial hash or use initial letter setting
      const hashLetter = this.handleInitialHash();
      const initialLetter = hashLetter || this.initialLetter;
      
      // Set active button based on initial letter
      const initialButton = Array.from(this.letterButtons).find(
        btn => btn.dataset.letter === initialLetter
      );
      if (initialButton) {
        this.updateActiveButton(initialButton);
      }
      
      this.observeVisibility();
      
      // Initial load with specified letter
      this.filterByLetter(initialLetter, true);
    }
  
    /**
     * Initialize sticky navigation for alphabet
     */
    initStickyNavigation() {
      if (!this.alphabetNav) return;
      
      // Use IntersectionObserver to detect when alphabet nav becomes sticky
      // Observe the title element to know when nav should show shadow effect
      const title = this.querySelector('.glossary-header h1');
      if (!title) return;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            // When title scrolls out of view, nav is stuck - add visual effect
            const navRect = this.alphabetNav.getBoundingClientRect();
            // Check if nav is at the top (stuck)
            if (navRect.top <= 1) {
              this.alphabetNav.classList.add('is-stuck');
            } else {
              this.alphabetNav.classList.remove('is-stuck');
            }
          });
        },
        {
          threshold: 0,
          rootMargin: '0px'
        }
      );
      
      observer.observe(title);
      
      // Also check on scroll for immediate feedback
      const handleScroll = () => {
        const navRect = this.alphabetNav.getBoundingClientRect();
        if (navRect.top <= 1) {
          this.alphabetNav.classList.add('is-stuck');
        } else {
          this.alphabetNav.classList.remove('is-stuck');
        }
      };
      
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
  
    /**
     * Bind event listeners
     */
    bindEvents() {
      // Letter button clicks
      this.letterButtons.forEach(button => {
        button.addEventListener('click', (e) => this.handleFilterClick(e));
      });
  
      // Load more button
      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener('click', () => this.loadMore());
      }
  
      // Handle browser back/forward
      window.addEventListener('hashchange', () => this.handleHashChange());
  
      // Keyboard navigation
      this.addEventListener('keydown', (e) => this.handleKeyboard(e));
    }
  
    /**
     * Check which letters have ingredients and mark unavailable buttons
     */
    checkAvailableLetters() {
      // Get all unique letters from items
      const availableLetters = new Set();
      this.glossaryItems.forEach(item => {
        const letter = item.dataset.letter;
        if (letter) {
          availableLetters.add(letter);
        }
      });
      
      // Mark buttons as unavailable if they have no ingredients
      this.letterButtons.forEach(button => {
        const letter = button.dataset.letter;
        
        // 'all' button is always available
        if (letter === 'all') {
          return;
        }
        
        // Mark as unavailable if no ingredients exist for this letter
        if (!availableLetters.has(letter)) {
          button.classList.add('unavailable');
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('tabindex', '-1');
        }
      });
    }
  
    /**
     * Handle filter button click
     * @param {Event} e - Click event
     */
    handleFilterClick(e) {
      const button = e.currentTarget;
      
      // Prevent action if button is unavailable
      if (button.classList.contains('unavailable')) {
        return;
      }
      
      const letter = button.dataset.letter;
      
      if (letter === this.currentFilter) return;
      
      // Reset to page 1 when changing filter
      this.currentPage = 1;
      this.filterByLetter(letter, true);
      this.updateURL(letter);
      this.updateActiveButton(button);
      
      // Scroll to top of results (removed smooth behavior to prevent flickering)
      // Use requestAnimationFrame to ensure DOM updates are complete before scrolling
      requestAnimationFrame(() => {
        this.resultsContainer?.scrollIntoView({ behavior: 'auto', block: 'start' });
      });
    }
  
    /**
     * Filter ingredients by letter
     * @param {string} letter - Letter to filter by
     * @param {boolean} resetPagination - Whether to reset pagination
     */
    filterByLetter(letter, resetPagination = false) {
      this.currentFilter = letter;
      
      // Refresh items list in case DOM changed
      this.glossaryItems = Array.from(
        this.resultsContainer?.querySelectorAll('.glossary-item') || 
        this.querySelectorAll('.glossary-item')
      );
      
      // First, hide all items
      this.glossaryItems.forEach(item => {
        item.classList.add('is-hidden');
        item.setAttribute('aria-hidden', 'true');
      });
      
      // Filter items based on letter
      this.filteredItems = this.glossaryItems.filter(item => {
        const itemLetter = item.dataset.letter;
        return letter === 'all' || itemLetter === letter;
      });
      
      if (resetPagination) {
        this.currentPage = 1;
      }
      
      // Show items for current page
      this.renderCurrentPage();
      
      // Update load more button
      this.updateLoadMoreButton();
      
      // Announce to screen readers
      this.announceResults(letter);
    }
  
    /**
     * Render items for current page
     */
    renderCurrentPage() {
      const startIndex = 0;
      const endIndex = this.currentPage * this.itemsPerPage;
      const itemsToShow = this.filteredItems.slice(startIndex, endIndex);
      
      itemsToShow.forEach(item => {
        item.classList.remove('is-hidden');
        item.removeAttribute('aria-hidden');
      });
    }
  
    /**
     * Load more items
     */
    loadMore() {
      if (!this.loadMoreButton) return;
      
      // Show loading state
      this.loadMoreButton.setAttribute('aria-busy', 'true');
      this.loadMoreButton.classList.add('is-loading');
      
      // Simulate smooth loading for better UX
      setTimeout(() => {
        this.currentPage++;
        this.renderCurrentPage();
        this.updateLoadMoreButton();
        
        // Remove loading state
        this.loadMoreButton.setAttribute('aria-busy', 'false');
        this.loadMoreButton.classList.remove('is-loading');
        
        // Focus on first newly loaded item for accessibility
        const firstNewItemIndex = (this.currentPage - 1) * this.itemsPerPage;
        const firstNewItem = this.filteredItems[firstNewItemIndex];
        if (firstNewItem) {
          firstNewItem.querySelector('.glossary-item__title')?.focus();
        }
        
        // Announce to screen readers
        const loadedCount = Math.min(
          this.currentPage * this.itemsPerPage,
          this.filteredItems.length
        );
        this.announceToScreenReader(
          `Loaded ${loadedCount} of ${this.filteredItems.length} ingredients`
        );
      }, 300);
    }
  
    /**
     * Update load more button visibility and text
     */
    updateLoadMoreButton() {
      if (!this.loadMoreButton) return;
      
      const totalItems = this.filteredItems.length;
      const loadedItems = Math.min(this.currentPage * this.itemsPerPage, totalItems);
      const hasMore = loadedItems < totalItems;
      
      if (hasMore) {
        this.loadMoreButton.classList.remove('is-hidden');
        this.loadMoreButton.removeAttribute('aria-hidden');
        
      } else {
        this.loadMoreButton.classList.add('is-hidden');
        this.loadMoreButton.setAttribute('aria-hidden', 'true');
      }
    }
  
    /**
     * Update active button state
     * @param {HTMLElement} activeButton - Button that was clicked
     */
    updateActiveButton(activeButton) {
      this.letterButtons.forEach(btn => {
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      });
      
      activeButton.classList.add('active');
      activeButton.setAttribute('aria-pressed', 'true');
    }
  
    /**
     * Update URL hash without scrolling
     * @param {string} letter - Letter to add to hash
     */
    updateURL(letter) {
      const newHash = letter === 'all' ? '' : `#letter-${letter}`;
      history.replaceState(null, null, newHash || window.location.pathname);
    }
  
    /**
     * Handle initial page load with hash
     * @returns {string|null} The letter from hash, or null if no hash
     */
    handleInitialHash() {
      const hash = window.location.hash;
      if (hash.startsWith('#letter-')) {
        const letter = hash.replace('#letter-', '');
        const button = Array.from(this.letterButtons)
          .find(btn => btn.dataset.letter === letter);
        
        if (button) {
          return letter;
        }
      }
      return null;
    }
  
    /**
     * Handle hash changes from browser navigation
     */
    handleHashChange() {
      const hash = window.location.hash;
      const letter = hash.startsWith('#letter-') 
        ? hash.replace('#letter-', '') 
        : 'all';
      
      const button = Array.from(this.letterButtons)
        .find(btn => btn.dataset.letter === letter);
      
      if (button && letter !== this.currentFilter) {
        this.currentPage = 1;
        this.filterByLetter(letter, true);
        this.updateActiveButton(button);
      }
    }
  
    /**
     * Handle keyboard navigation
     * @param {KeyboardEvent} e - Keyboard event
     */
    handleKeyboard(e) {
      if (!e.target.classList.contains('glossary-letter')) return;
      
      // Filter out unavailable buttons for navigation
      const availableButtons = Array.from(this.letterButtons).filter(
        btn => !btn.classList.contains('unavailable')
      );
      const currentIndex = availableButtons.indexOf(e.target);
      let targetButton;
      
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          targetButton = availableButtons[currentIndex + 1] || availableButtons[0];
          break;
        case 'ArrowLeft':
          e.preventDefault();
          targetButton = availableButtons[currentIndex - 1] || availableButtons[availableButtons.length - 1];
          break;
        case 'Home':
          e.preventDefault();
          targetButton = availableButtons[0];
          break;
        case 'End':
          e.preventDefault();
          targetButton = availableButtons[availableButtons.length - 1];
          break;
        default:
          return;
      }
      
      if (targetButton) {
        targetButton.focus();
      }
    }
  
    /**
     * Announce filter results to screen readers
     * @param {string} letter - Current filter letter
     */
    announceResults(letter) {
      const totalCount = this.filteredItems.length;
      const loadedCount = Math.min(this.currentPage * this.itemsPerPage, totalCount);
      
      const message = letter === 'all' 
        ? `Showing ${loadedCount} of ${totalCount} ingredients`
        : `Showing ${loadedCount} of ${totalCount} ingredients starting with ${letter.toUpperCase()}`;
      
      this.announceToScreenReader(message);
    }
  
    /**
     * Create live region announcement
     * @param {string} message - Message to announce
     */
    announceToScreenReader(message) {
      let liveRegion = this.querySelector('[aria-live]');
      
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.className = 'sr-only';
        this.appendChild(liveRegion);
      }
      
      liveRegion.textContent = message;
    }
  
    /**
     * Observe element visibility for animations
     */
    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !entry.target.classList.contains('is-hidden')) {
              entry.target.classList.add('is-visible');
            }
          });
        },
        { threshold: 0.1 }
      );
      
      this.glossaryItems.forEach(item => observer.observe(item));
    }
  }
  
  // Register custom element
  if (!customElements.get('ingredient-glossary')) {
    customElements.define('ingredient-glossary', IngredientGlossary);
  }